import {NgZone} from '@angular/core';

/**
 * Options shared by the coalescing helpers
 */
export interface CoalescingOptions {
    /**
     * Time to wait for more requests before running the handler, in milliseconds
     */
    debounceMS?: number;
    /**
     * Maximum time a request can be delayed by a continuous stream of requests, in milliseconds.
     * `0` disables the cap, meaning a never ending stream can postpone the handler forever
     */
    maxWaitMS?: number;
    /**
     * Angular zone. When provided, the debounce timer is scheduled outside of Angular so that
     * incoming requests do not each trigger their own change detection pass, and the handler is
     * run back inside Angular
     */
    zone?: NgZone;
    /**
     * Called when the handler throws / rejects. Defaults to logging in the console
     * @param err Thrown error
     */
    onError?: (err: unknown) => void;
}

/**
 * Runs a handler at most once per burst of requests.
 *
 * Typical use case: a stream of server side events where each event would otherwise trigger its own
 * expensive reload and change detection pass, freezing the browser.
 *
 * The handler is never run concurrently with itself: a request made while the handler is still
 * running is replayed once it completes.
 *
 * @example
 * private readonly _refreshRunner = new CoalescedRunner(() => this.refresh(), {
 *     debounceMS: 300,
 *     maxWaitMS: 2000,
 *     zone: this._ngZone
 * });
 *
 * // In the event handler
 * this._refreshRunner.request();
 *
 * // In onDestroy
 * this._refreshRunner.dispose();
 */
export class CoalescedRunner {
    private readonly _handler: () => void | Promise<void>;
    private readonly _debounceMS: number;
    private readonly _maxWaitMS: number;
    private readonly _zone?: NgZone;
    private readonly _onError: (err: unknown) => void;

    private _timer: ReturnType<typeof setTimeout> | undefined;
    private _firstRequestMS: number | undefined;
    private _running = false;
    private _pending = false;
    private _disposed = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     * @param handler Handler to run
     * @param opts Coalescing options
     */
    constructor(handler: () => void | Promise<void>, opts: CoalescingOptions = {}) {
        this._handler = handler;
        this._debounceMS = opts.debounceMS ?? 300;
        this._maxWaitMS = opts.maxWaitMS ?? 0;
        this._zone = opts.zone;
        this._onError =
            opts.onError ??
            ((err: unknown) => {
                // Log error in the console for debugging, do not remove
                console.log(err);
            });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Whether the handler is currently running
     */
    public get isRunning(): boolean {
        return this._running;
    }
    /**
     * Whether a run is scheduled but has not started yet
     */
    public get isScheduled(): boolean {
        return this._timer !== undefined;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Request a run, restarting the debounce window
     */
    public request(): void {
        if (this._disposed) return;

        const nowMS = Date.now();
        this._firstRequestMS ??= nowMS;

        let delayMS = this._debounceMS;
        if (this._maxWaitMS > 0) {
            // Never delay further than the max wait, so a continuous stream of requests cannot starve the handler
            const remainingMaxWaitMS = this._maxWaitMS - (nowMS - this._firstRequestMS);
            delayMS = Math.min(delayMS, remainingMaxWaitMS);
        }
        delayMS = Math.max(0, delayMS);

        this.clearTimer();
        this.schedule(delayMS);
    }
    /**
     * Run the handler right now, cancelling any scheduled run
     */
    public flush(): void {
        if (this._disposed) return;

        this.cancel();
        this.run();
    }
    /**
     * Cancel any scheduled run (a run already in progress is not interrupted)
     */
    public cancel(): void {
        this.clearTimer();
        this._firstRequestMS = undefined;
        this._pending = false;
    }
    /**
     * Cancel any scheduled run and prevent any further one
     */
    public dispose(): void {
        this._disposed = true;
        this.cancel();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Schedule the run, outside of Angular when a zone is available
     * @param delayMS Delay in milliseconds
     */
    private schedule(delayMS: number): void {
        const startTimer = () => {
            this._timer = setTimeout(() => {
                this._timer = undefined;
                this._firstRequestMS = undefined;
                if (this._disposed) return;

                if (this._zone) this._zone.run(() => this.run());
                else this.run();
            }, delayMS);
        };

        if (this._zone) this._zone.runOutsideAngular(startTimer);
        else startTimer();
    }
    /**
     * Run the handler, making sure it never runs concurrently with itself
     */
    private async run(): Promise<void> {
        if (this._running) {
            this._pending = true;
            return;
        }

        this._running = true;
        try {
            do {
                this._pending = false;

                try {
                    await this._handler();
                } catch (err: unknown) {
                    this._onError(err);
                }
            } while (this._pending && !this._disposed);
        } finally {
            this._running = false;
        }
    }
    /**
     * Clear the pending timer, if any
     */
    private clearTimer(): void {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = undefined;
        }
    }
}

// ===============================================================
// ===============================================================

/**
 * Buffers keyed updates and applies them in a single batch.
 *
 * Only the latest value of a given key is kept, so repeated updates of the same entity collapse
 * into one. Combined with a zone, a burst of updates results in a single change detection pass
 * instead of one per update.
 *
 * @example
 * private readonly _statusBuffer = new KeyedUpdateBuffer<string, EStatus>(
 *     (updates) => this.applyStatuses(updates),
 *     {debounceMS: 16, zone: this._ngZone}
 * );
 *
 * // In the event handler
 * this._statusBuffer.set(taskUUID, status);
 *
 * // In onDestroy
 * this._statusBuffer.dispose();
 */
export class KeyedUpdateBuffer<t_Key, t_Value> {
    private readonly _pending = new Map<t_Key, t_Value>();
    private readonly _runner: CoalescedRunner;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     * @param handler Handler applying the batch of updates
     * @param opts Coalescing options (defaults to one animation frame of buffering)
     */
    constructor(handler: (updates: Map<t_Key, t_Value>) => void | Promise<void>, opts: CoalescingOptions = {}) {
        this._runner = new CoalescedRunner(
            () => {
                if (this._pending.size === 0) return;

                // Hand over a snapshot so that updates received while the handler runs are not lost
                const batch = new Map(this._pending);
                this._pending.clear();

                return handler(batch);
            },
            {...opts, debounceMS: opts.debounceMS ?? 16}
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Number of buffered updates
     */
    public get size(): number {
        return this._pending.size;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Buffer an update, replacing any previous value for the same key
     * @param key Update key
     * @param value Update value
     */
    public set(key: t_Key, value: t_Value): void {
        this._pending.set(key, value);
        this._runner.request();
    }
    /**
     * Apply the buffered updates right now
     */
    public flush(): void {
        this._runner.flush();
    }
    /**
     * Drop the buffered updates
     */
    public cancel(): void {
        this._pending.clear();
        this._runner.cancel();
    }
    /**
     * Drop the buffered updates and prevent any further one
     */
    public dispose(): void {
        this._pending.clear();
        this._runner.dispose();
    }
}
